import { useState, useEffect, useMemo, useRef, useCallback } from "react";
function areOrderingChoicesEqual(left, right) {
  return left.length === right.length && left.every((choice, index) => {
    var _a, _b, _c, _d;
    const other = right[index];
    return choice.key === other.key && choice.answer === other.answer && ((_a = choice.image) == null ? void 0 : _a.signedUrl) === ((_b = other.image) == null ? void 0 : _b.signedUrl) && ((_c = choice.image) == null ? void 0 : _c.url) === ((_d = other.image) == null ? void 0 : _d.url);
  });
}
function useOrderingQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  const [startTime] = useState(() => Date.now());
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const [isVertical, setIsVertical] = useState(
    () => typeof window !== "undefined" ? window.innerWidth < 900 : true
  );
  const isSolved = solveResult !== null && solveResult !== void 0;
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  useEffect(() => {
    const handleResize = () => {
      setIsVertical(window.innerWidth < 900);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const computedInitialChoices = useMemo(() => {
    if (initialAnswersProp !== void 0) {
      const submittedChoices2 = initialAnswersProp.map((key) => quiz.choices.find((c) => c.key === key)).filter((c) => c !== void 0);
      return submittedChoices2.length > 0 ? submittedChoices2 : [...quiz.choices];
    }
    if (isViewSolved && (solveResult == null ? void 0 : solveResult.correctAnswers) && solveResult.correctAnswers.length > 0) {
      return solveResult.correctAnswers.map((key) => quiz.choices.find((c) => c.key === key)).filter((c) => c !== void 0);
    }
    return [...quiz.choices];
  }, [quiz.choices, initialAnswersProp, isViewSolved, solveResult == null ? void 0 : solveResult.correctAnswers]);
  const [orderedChoices, setOrderedChoices] = useState(computedInitialChoices);
  const [submittedChoices, setSubmittedChoices] = useState(null);
  const onAnswerChangeRef = useRef(onAnswerChange);
  useEffect(() => {
    onAnswerChangeRef.current = onAnswerChange;
  }, [onAnswerChange]);
  useEffect(() => {
    setOrderedChoices(
      (prev) => areOrderingChoicesEqual(prev, computedInitialChoices) ? prev : computedInitialChoices
    );
  }, [computedInitialChoices]);
  useEffect(() => {
    if (isSolved && !submittedChoices) {
      setSubmittedChoices(orderedChoices);
    }
  }, [isSolved, submittedChoices, orderedChoices]);
  useEffect(() => {
    if (!isSolved) return;
    if (shouldShowCorrectAnswers && (solveResult == null ? void 0 : solveResult.correctAnswers) && solveResult.correctAnswers.length > 0) {
      const reordered = solveResult.correctAnswers.map((key) => quiz.choices.find((c) => c.key === key)).filter((c) => c !== void 0);
      setOrderedChoices(
        (prev) => areOrderingChoicesEqual(prev, reordered) ? prev : reordered
      );
    } else if (submittedChoices) {
      setOrderedChoices(
        (prev) => areOrderingChoicesEqual(prev, submittedChoices) ? prev : submittedChoices
      );
    }
  }, [shouldShowCorrectAnswers, isSolved, solveResult == null ? void 0 : solveResult.correctAnswers, quiz.choices, submittedChoices]);
  useEffect(() => {
    var _a;
    (_a = onAnswerChangeRef.current) == null ? void 0 : _a.call(onAnswerChangeRef, orderedChoices.map((c) => c.key));
  }, [orderedChoices]);
  const handleReorder = useCallback((oldIndex, newIndex) => {
    setOrderedChoices((prev) => {
      const next = [...prev];
      const [removed] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, removed);
      return next;
    });
  }, []);
  const canSubmit = true;
  const handleSubmit = useCallback(() => {
    if (isSolved) return;
    const elapsedTime = Date.now() - startTime;
    const answers = orderedChoices.map((c) => c.key);
    onSolve == null ? void 0 : onSolve(quizId, answers, quiz.vars, elapsedTime);
  }, [isSolved, startTime, orderedChoices, onSolve, quizId, quiz.vars]);
  return {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleReorder,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  useOrderingQuiz
};
//# sourceMappingURL=use-ordering-quiz.js.map
